import React, { useState } from 'react';
import { Alert, Button, Select, Space, Table, Tag, Typography, theme } from 'antd';
import { InboxOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { PageHeader, CardHeader } from 'components/headings';
import useDataRequestsList from 'hooks/useDataRequestsList';
import useTenantFlags from 'hooks/useTenantFlags';
import { ESTADOS_SOLICITUD, TIPOS_SOLICITUD } from 'api/dataRequests';
import SolicitudesDatosKpiStrip from './SolicitudesDatosKpiStrip';
import SolicitudDatosDrawer from './SolicitudDatosDrawer';
import { canalLabel, estadoColor, estadoLabel, tipoLabel } from './solicitudesDatosLabels';
import { calcularPlazo, colorPlazo, formatearFecha } from './vencimiento';

const { Text } = Typography;
const { useToken } = theme;

// Nació de un bug donde el rowKey colisionaba: se exporta para que la Table y
// cualquier test usen literalmente la misma clave.
export const SOLICITUDES_DATOS_ROW_KEY = 'id';

const TODOS = '__todos';

function SolicitudesDatosPage() {
  const { token } = useToken();
  const { readOnly } = useTenantFlags();
  const {
    rows,
    total,
    vencidas,
    loading,
    error,
    page,
    setPage,
    pageSize,
    filters,
    setFilter,
    refresh,
  } = useDataRequestsList();

  const [seleccionada, setSeleccionada] = useState(null);

  const panelStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    overflow: 'hidden',
    background: token.colorBgContainer,
    boxShadow: token.boxShadowSecondary,
  };

  const filtrando = Boolean(filters.estado || filters.tipo);

  const columns = [
    {
      title: 'Persona',
      key: 'solicitante',
      render: (_, row) => (
        <div style={{ minWidth: 0 }}>
          <Text style={{ display: 'block' }}>
            {row.solicitante_nombre || row.solicitante_rut || 'Sin identificar'}
          </Text>
          {row.solicitante_email && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.solicitante_email}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Qué pide',
      dataIndex: 'tipo',
      width: 170,
      render: (tipo) => tipoLabel(tipo),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 140,
      render: (estado) => <Tag color={estadoColor(estado)}>{estadoLabel(estado)}</Tag>,
    },
    {
      title: 'Llegó por',
      dataIndex: 'canal',
      width: 150,
      render: (canal) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {canalLabel(canal)}
        </Text>
      ),
    },
    {
      title: 'Recibida',
      dataIndex: 'recibida_at',
      width: 120,
      render: (valor) => formatearFecha(valor),
    },
    {
      title: 'Plazo para responder',
      dataIndex: 'vence_at',
      width: 190,
      render: (_, row) => {
        const plazo = calcularPlazo(row);
        if (!plazo) return '—';
        const color = colorPlazo(plazo, token);
        return (
          <div style={{ minWidth: 0 }}>
            <Text style={{ color, fontWeight: plazo.vencida ? 600 : 400 }}>
              {formatearFecha(row.vence_at)}
            </Text>
            {plazo.texto && (
              <>
                <br />
                <Text style={{ color, fontSize: 12 }}>{plazo.texto}</Text>
              </>
            )}
          </div>
        );
      },
    },
  ];

  if (error) {
    // El 403 no es una falla: es que este usuario no tiene por qué ver esto.
    // Merece un mensaje que lo diga, no un "algo salió mal" con botón inútil.
    const prohibido = error.forbidden;
    return (
      <div style={{ paddingTop: token.paddingLG }}>
        <Alert
          type={prohibido ? 'warning' : 'error'}
          showIcon
          message={
            prohibido
              ? 'No tienes permiso para ver esta sección'
              : 'No pudimos cargar las solicitudes'
          }
          description={
            prohibido
              ? 'Las solicitudes sobre datos personales solo pueden verlas los perfiles autorizados. Si necesitas acceso, contacta al responsable de tu organización.'
              : error.mensaje || 'Ocurrió un problema al obtener el listado. Inténtalo de nuevo.'
          }
          action={
            prohibido ? null : (
              <Button type="primary" onClick={refresh}>
                Reintentar
              </Button>
            )
          }
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={SafetyOutlined}
        title="Solicitudes sobre datos personales"
        subtitle="Peticiones de las personas sobre los datos que guardamos de ellas. Cada una tiene un plazo legal para responderse."
        actions={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Actualizar
            </Button>
          </Space>
        }
      />

      <SolicitudesDatosKpiStrip
        total={total}
        vencidas={vencidas}
        loading={loading}
        filtrando={filtrando}
      />

      <div style={panelStyle}>
        <CardHeader
          icon={InboxOutlined}
          tint={token.colorInfo}
          title="Bandeja de solicitudes"
          subtitle="Ordenadas por urgencia: primero las que vencen antes"
          action={
            <Space wrap>
              <Select
                style={{ width: 180 }}
                value={filters.estado ?? TODOS}
                onChange={(v) => setFilter({ estado: v === TODOS ? undefined : v })}
                options={[
                  { value: TODOS, label: 'Todos los estados' },
                  ...ESTADOS_SOLICITUD.map((e) => ({ value: e, label: estadoLabel(e) })),
                ]}
              />
              <Select
                style={{ width: 200 }}
                value={filters.tipo ?? TODOS}
                onChange={(v) => setFilter({ tipo: v === TODOS ? undefined : v })}
                options={[
                  { value: TODOS, label: 'Todo lo que piden' },
                  ...TIPOS_SOLICITUD.map((t) => ({ value: t, label: tipoLabel(t) })),
                ]}
              />
            </Space>
          }
        />
        <div style={{ padding: token.paddingSM }}>
          <Table
            rowKey={SOLICITUDES_DATOS_ROW_KEY}
            loading={loading}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 900 }}
            onRow={(row) => ({
              onClick: () => setSeleccionada(row),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: filtrando
                ? 'No hay solicitudes que coincidan con el filtro.'
                : 'Todavía no hay solicitudes sobre datos personales.',
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              onChange: setPage,
              showSizeChanger: false,
            }}
          />
        </div>
      </div>

      <SolicitudDatosDrawer
        open={Boolean(seleccionada)}
        solicitud={seleccionada}
        readOnly={readOnly}
        onClose={() => setSeleccionada(null)}
        onSaved={refresh}
      />
    </div>
  );
}

export default SolicitudesDatosPage;
